import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink, Users, GraduationCap, Receipt, PoundSterling, TrendingUp, Activity, Eye, UserPlus, Globe, Search, Monitor, Smartphone, Tablet, PlayCircle } from "lucide-react";
import api from "../../lib/api";

const POSTHOG_HOST_FALLBACK = "https://eu.posthog.com";

// Tiny inline sparkline — no chart library.
function Sparkline({ data, color = "#5D2C8F", height = 52 }) {
  const max = Math.max(1, ...data);
  const w = 240, h = height, pad = 4;
  const step = data.length > 1 ? (w - pad * 2) / (data.length - 1) : 0;
  const pts = data.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - (v / max) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const area = `${pad},${h - pad} ${pts} ${(pad + (data.length - 1) * step).toFixed(1)},${h - pad}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[52px]">
      <polygon points={area} fill={color} opacity="0.12" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function StatCard({ label, value, Icon, tint, sub }) {
  return (
    <div className="admin-surface p-5" style={{ borderTop: `3px solid ${tint}` }}>
      <div className="flex items-center gap-2 text-xs text-[#6E6074] uppercase tracking-wide">
        <Icon size={14} style={{ color: tint }} /> {label}
      </div>
      <div className="font-display text-3xl text-[#5D2C8F] mt-1">{value}</div>
      {sub && <div className="text-xs text-[#8A7C92] mt-1">{sub}</div>}
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [web, setWeb] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get(`/admin/analytics/overview?days=${days}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/admin/analytics/web/overview?days=${days}`).then((r) => setWeb(r.data)).catch((e) => {
      console.warn("[web analytics] fetch failed:", e);
      setWeb({ configured: false, error: e?.response?.data?.detail || "Failed to load" });
    });
    api.get(`/admin/analytics/web/recordings?limit=8`).then((r) => setRecordings(r.data.recordings || [])).catch(() => setRecordings([]));
  }, [days]);

  const posthogHost = web?.posthog_host || POSTHOG_HOST_FALLBACK;

  const leadToStudent = useMemo(() => {
    if (!data) return 0;
    const f = data.funnel;
    return f.leads ? Math.round((f.students / f.leads) * 100) : 0;
  }, [data]);

  const studentToPaid = useMemo(() => {
    if (!data) return 0;
    const f = data.funnel;
    return f.students ? Math.round((f.paid / f.students) * 100) : 0;
  }, [data]);

  if (!data) return <div className="text-[#6E6074]">Loading analytics…</div>;

  const leadSeries = data.series.map((d) => d.leads);
  const studentSeries = data.series.map((d) => d.students);
  const paidSeries = data.series.map((d) => d.paid);
  const revenueSeries = data.series.map((d) => d.revenue);

  const sessionLabels = { tue_am: "Tuesday Morning", tue_pm: "Tuesday Afternoon", fri_eve: "Friday Evening", unassigned: "Unassigned" };

  return (
    <div data-testid="admin-analytics-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">how the studio is doing</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Analytics</h1>
          <p className="text-sm text-[#6E6074] mt-1">Live CRM metrics from your database. For website traffic, session recordings and funnels, open the PostHog dashboard.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="bg-white border border-[#E5DCE6] rounded-lg px-3 py-2 text-sm text-[#5D2C8F]"
            data-testid="analytics-range"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
          </select>
          <a
            href={posthogHost}
            target="_blank"
            rel="noreferrer"
            className="sac-btn-primary !py-2 !px-4 text-sm"
            data-testid="analytics-posthog-btn"
          >
            Open PostHog <ExternalLink size={14} />
          </a>
        </div>
      </div>

      {/* Totals for range */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <StatCard label={`Leads / ${days}d`} value={data.totals.leads} Icon={Users} tint="#A685BD" sub="New enquiries via the contact form" />
        <StatCard label={`Students / ${days}d`} value={data.totals.students} Icon={GraduationCap} tint="#8EC9C7" sub="Leads converted to enrolled students" />
        <StatCard label={`Paid invoices / ${days}d`} value={data.totals.paid} Icon={Receipt} tint="#E8C37A" sub="Marked fully paid" />
        <StatCard label={`Revenue / ${days}d`} value={`£${data.totals.revenue.toLocaleString()}`} Icon={PoundSterling} tint="#5D2C8F" sub="From paid invoices in window" />
      </div>

      {/* Sparklines */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <TrendCard title="New leads" series={leadSeries} color="#A685BD" />
        <TrendCard title="New students" series={studentSeries} color="#8EC9C7" />
        <TrendCard title="Invoices paid" series={paidSeries} color="#E8C37A" />
        <TrendCard title="Revenue (£)" series={revenueSeries} color="#5D2C8F" formatter={(v) => `£${v.toFixed(0)}`} />
      </div>

      {/* Funnel + session mix */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="admin-surface p-5">
          <div className="flex items-center gap-2 text-xs text-[#6E6074] uppercase tracking-wide mb-3">
            <TrendingUp size={14} style={{ color: "#5D2C8F" }} /> Conversion funnel (all-time)
          </div>
          <FunnelRow label="Enquiries received" value={data.funnel.leads} pct={100} color="#A685BD" />
          <FunnelRow label="Warm or hot leads" value={data.funnel.warm_hot} pct={data.funnel.leads ? Math.round((data.funnel.warm_hot / data.funnel.leads) * 100) : 0} color="#E8C37A" />
          <FunnelRow label="Enrolled as student" value={data.funnel.students} pct={leadToStudent} color="#8EC9C7" />
          <FunnelRow label="Paid in full" value={data.funnel.paid} pct={studentToPaid} color="#5D2C8F" />
          <p className="text-xs text-[#8A7C92] mt-3">{leadToStudent}% of enquiries become enrolled students. {studentToPaid}% of students have paid in full.</p>
        </div>

        <div className="admin-surface p-5">
          <div className="flex items-center gap-2 text-xs text-[#6E6074] uppercase tracking-wide mb-3">
            <Activity size={14} style={{ color: "#5D2C8F" }} /> Current-term session mix
          </div>
          {Object.keys(data.session_mix).length === 0 ? (
            <div className="text-sm text-[#6E6074] italic">No current-term students yet.</div>
          ) : (
            Object.entries(data.session_mix).map(([k, v]) => {
              const total = Object.values(data.session_mix).reduce((a, b) => a + b, 0);
              const pct = total ? Math.round((v / total) * 100) : 0;
              return <FunnelRow key={k} label={sessionLabels[k] || k} value={v} pct={pct} color="#8EC9C7" />;
            })
          )}
        </div>
      </div>

      {/* -------- Web analytics (PostHog-powered) -------- */}
      <div className="mb-3 mt-10 border-t border-[#E5DCE6] pt-6">
        <div className="font-hand text-[#8EC9C7] text-xl">from your website visitors</div>
        <h2 className="font-display text-3xl text-[#5D2C8F]">Web analytics</h2>
        <p className="text-sm text-[#6E6074] mt-1 max-w-3xl">Live traffic, top pages, referrers, devices and conversions — the same numbers you'd get from Wix or Google Analytics, powered by PostHog.</p>
      </div>

      {!web ? (
        <div className="admin-surface p-5 text-sm text-[#6E6074]">Loading web analytics…</div>
      ) : !web.configured ? (
        <div className="admin-surface p-5 border-l-4 border-[#E8C37A]">
          <div className="font-display text-lg text-[#5D2C8F] mb-1">PostHog not configured yet</div>
          <p className="text-sm text-[#6E6074]">{web.message || web.error || "Add your PostHog Personal API key to backend/.env to see live web traffic here."}</p>
        </div>
      ) : (
        <>
          {/* Totals */}
          <div className="grid md:grid-cols-4 gap-4 mb-6" data-testid="web-analytics-totals">
            <StatCard label={`Page views / ${web.days}d`} value={web.totals.pageviews.toLocaleString()} Icon={Eye} tint="#A685BD" sub="Every page load on the public site" />
            <StatCard label={`Unique visitors / ${web.days}d`} value={web.totals.visitors.toLocaleString()} Icon={UserPlus} tint="#8EC9C7" sub="Distinct people" />
            <StatCard label={`Sessions / ${web.days}d`} value={web.totals.sessions.toLocaleString()} Icon={Activity} tint="#E8C37A" sub="Browsing sessions (30-min idle window)" />
            <StatCard label="Conversions" value={(web.conversions.enquiry_submits + web.conversions.payment_success_visitors).toLocaleString()} Icon={Receipt} tint="#5D2C8F" sub={`${web.conversions.enquiry_submits} enquiries · ${web.conversions.payment_success_visitors} payment-success`} />
          </div>

          {/* Daily sparklines */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <TrendCard title="Daily page views" series={web.series.map(d => d.pv)} color="#A685BD" />
            <TrendCard title="Daily visitors" series={web.series.map(d => d.uv)} color="#8EC9C7" />
          </div>

          {/* Top pages + referrers */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <ListCard title="Top pages" icon={Globe} rows={web.top_pages} labelKey="path" valueKey="views" emptyText="No page views captured yet — visit the public site to start tracking." testid="top-pages" />
            <ListCard title="Where visitors came from" icon={ExternalLink} rows={web.referrers} labelKey="source" valueKey="views" emptyText="No referrers yet. Direct traffic doesn't show here." testid="referrers" />
          </div>

          {/* Search terms + devices/browsers */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <ListCard title="Search terms (UTM / query)" icon={Search} rows={web.search_terms} labelKey="term" valueKey="views" emptyText="No search-term data captured yet." testid="search-terms" />
            <div className="admin-surface p-5" data-testid="devices-card">
              <div className="flex items-center gap-2 text-xs text-[#6E6074] uppercase tracking-wide mb-3">
                <Monitor size={14} style={{ color: "#5D2C8F" }} /> Devices
              </div>
              {web.devices.length === 0 ? (
                <div className="text-sm text-[#6E6074] italic">No device data yet.</div>
              ) : (
                <div className="space-y-2">
                  {web.devices.map((d, i) => {
                    const total = web.devices.reduce((a, b) => a + b.visitors, 0);
                    const pct = total ? Math.round((d.visitors / total) * 100) : 0;
                    const Ico = d.device === "Mobile" ? Smartphone : d.device === "Tablet" ? Tablet : Monitor;
                    return (
                      <div key={d.device || `device-${i}`} className="flex items-center gap-3" data-testid={`device-row-${i}`}>
                        <Ico size={16} style={{ color: "#8EC9C7" }} />
                        <div className="flex-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#5D2C8F]">{d.device || "Unknown"}</span>
                            <span className="text-[#6E6074]">{d.visitors} · {pct}%</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-[#F4EDDD] mt-1 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "#8EC9C7" }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {web.browsers?.length > 0 && (
                <div className="mt-5 pt-4 border-t border-[#F4EDDD]">
                  <div className="text-xs text-[#6E6074] uppercase tracking-wide mb-2">Top browsers</div>
                  <div className="flex flex-wrap gap-1.5">
                    {web.browsers.map((b, i) => (
                      <span key={b.browser || `browser-${i}`} className="text-xs px-2 py-0.5 rounded-full bg-[#F4EDDD] text-[#5D2C8F]">{b.browser} · {b.visitors}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Session recordings */}
          <div className="admin-surface p-5 mb-6" data-testid="recordings-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-xs text-[#6E6074] uppercase tracking-wide">
                <PlayCircle size={14} style={{ color: "#5D2C8F" }} /> Recent session recordings
              </div>
              <a href={`${posthogHost}/replay`} target="_blank" rel="noreferrer" className="text-xs text-[#5D2C8F] underline">all recordings →</a>
            </div>
            {recordings.length === 0 ? (
              <div className="text-sm text-[#6E6074] italic">No session recordings yet. They'll appear here as visitors browse the public site.</div>
            ) : (
              <ul className="divide-y divide-[#F4EDDD]">
                {recordings.map((rec) => (
                  <li key={rec.id} className="py-2 flex items-center justify-between text-sm" data-testid={`recording-${rec.id}`}>
                    <div className="min-w-0">
                      <div className="text-[#5D2C8F] truncate">{rec.person || "Anonymous visitor"}</div>
                      <div className="text-xs text-[#8A7C92]">{rec.start ? new Date(rec.start).toLocaleString("en-GB") : ""} · {Math.round((rec.duration || 0))}s</div>
                    </div>
                    {rec.url && <a href={rec.url} target="_blank" rel="noreferrer" className="text-xs px-3 py-1 rounded-full bg-[#F4EDDD] text-[#5D2C8F] hover:bg-[#E8C37A]/30">Watch</a>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ListCard({ title, icon: Icon, rows, labelKey, valueKey, emptyText, testid }) {
  const total = Array.isArray(rows) ? rows.reduce((a, r) => a + (r[valueKey] || 0), 0) : 0;
  return (
    <div className="admin-surface p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-[#6E6074] uppercase tracking-wide mb-3">
        <Icon size={14} style={{ color: "#5D2C8F" }} /> {title}
      </div>
      {!rows || rows.length === 0 ? (
        <div className="text-sm text-[#6E6074] italic">{emptyText}</div>
      ) : (
        <ul className="space-y-2">
          {rows.slice(0, 8).map((r, i) => {
            const pct = total ? Math.round((r[valueKey] / total) * 100) : 0;
            return (
              <li key={r[labelKey] || `row-${i}`} className="text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[#5D2C8F] truncate pr-2" title={r[labelKey]}>{r[labelKey] || "—"}</span>
                  <span className="text-[#6E6074] whitespace-nowrap">{r[valueKey]} · {pct}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#F4EDDD] mt-1 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "#A685BD" }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TrendCard({ title, series, color, formatter }) {
  const total = series.reduce((a, b) => a + b, 0);
  const shown = formatter ? formatter(total) : total;
  return (
    <div className="admin-surface p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-[#6E6074] uppercase tracking-wide">{title}</div>
        <div className="font-display text-2xl" style={{ color }}>{shown}</div>
      </div>
      <Sparkline data={series} color={color} />
    </div>
  );
}

function FunnelRow({ label, value, pct, color }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-[#5D2C8F]">{label}</span>
        <span className="text-[#6E6074]">{value} · {pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-[#F4EDDD] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
